import {
  Box,
  Radio as RadioBase,
  type RadioProps as RadioPropsBase,
} from '@mantine/core';
import { forwardRef } from 'react';

export type RadioProps = RadioPropsBase & {
  box?: boolean;
};

/**
 * RadioField component
 *
 * @example
 * ```tsx
 * <Radio name="field.name" box />
 * ```
 *
 * @public
 * @param box - render radio as a box
 * @param checked - checked state
 * @param props - other props
 */
export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({ box, checked, ...props }, ref) => {
    const RadioRender = <RadioBase checked={checked} ref={ref} {...props} />;

    if (!box) {
      return RadioRender;
    }

    return (
      <Box
        component="label"
        sx={theme => ({
          background: checked ? theme.colors.violet[1] : theme.colors.white,
          border: checked
            ? `solid 1px ${theme.colors.violet[0]}`
            : `solid 1px ${theme.colors.gray[3]}`,
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.sm,
          padding: theme.spacing.sm,
          borderRadius: theme.radius.sm,
          '&:hover': {
            cursor: 'pointer !important',
          },
        })}
      >
        {RadioRender}
      </Box>
    );
  },
);
